import { useAuth } from '../composables/useAuth';

const API_BASE = import.meta.env.VITE_API_BASE || '/api/v1';

export interface ApiFetchOptions extends RequestInit {
  responseType?: 'json' | 'blob';
  headers?: Record<string, string>;
  skipAuthRedirect?: boolean; // Option pour éviter la redirection automatique
}

export async function apiFetch(path: string, options?: ApiFetchOptions) {
  const { getAuthHeaders } = useAuth();
  
  const headers: Record<string, string> = { ...(options?.headers ?? {}) };
  const authHeaders = getAuthHeaders();
  Object.assign(headers, authHeaders);
  
  // Ajouter Content-Type pour JSON si body est présent et non FormData
  if (options?.body && !(options.body instanceof FormData)) {
    if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
  }
  
  const init: RequestInit = {
    method: options?.method ?? 'GET',
    headers
  };
  
  if (options?.body) {
    init.body = options.body instanceof FormData 
      ? options.body 
      : JSON.stringify(options.body);
  }
  
  const res = await fetch(`${API_BASE}${path}`, init);
  
  if (!res.ok) {
    // Si erreur 401 (Unauthorized), tenter un silent refresh avant de déconnecter
    if (res.status === 401 && !options?.skipAuthRedirect) {
      try {
        const { silentRefresh } = await import('./tokenRefresh');
        const newToken = await silentRefresh();
        const retryHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
        const retry = await fetch(`${API_BASE}${path}`, { ...init, headers: retryHeaders });
        if (retry.ok) {
          if (options?.responseType === 'blob') return retry.blob();
          const retryText = await retry.text();
          return retryText ? JSON.parse(retryText) : undefined;
        }
      } catch {
        // Refresh failed — fall through to logout
      }
      await useAuth().logout();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(new Error('Session expirée. Veuillez vous reconnecter.'));
    }

    const message = await safeReadError(res);
    throw new Error(message ?? `Erreur API ${res.status}`);
  }

  if (options?.responseType === 'blob') {
    return res.blob();
  }

  return res.json();
}

async function safeReadError(response: Response) {
  try {
    const payload = await response.json();
    return payload?.message;
  } catch {
    return response.statusText;
  }
}

